import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import { comics, featuredComicIds, hotComicIds, newUpdateComicIds, getComicById } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { siteConfig } from '@/mocks/siteConfig';

export default function HomePage() {
  const featuredComics = useMemo(
    () => featuredComicIds.map((id) => getComicById(id)).filter(Boolean),
    [],
  );
  const hotComics = useMemo(
    () => hotComicIds.map((id) => getComicById(id)).filter(Boolean),
    [],
  );
  const newUpdateComics = useMemo(
    () => newUpdateComicIds.map((id) => getComicById(id)).filter(Boolean),
    [],
  );

  const heroComic = featuredComics[0];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: siteConfig.fullTitle,
    description: siteConfig.description,
    url: siteConfig.url,
  };

  return (
    <>
      <SEOHead
        title={siteConfig.fullTitle}
        description={siteConfig.description}
        keywords={siteConfig.keywords}
        canonicalUrl={siteConfig.url}
        schemaObjects={[organizationSchema, webSiteSchema, webPageSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20magical%20general%20store%20interior%20with%20wooden%20shelves%20full%20of%20mysterious%20objects%2C%20soft%20golden%20light%20streaming%20through%20windows%2C%20anime%20art%20style%2C%20illustration%2C%20warm%20amber%20and%20brown%20tones%2C%20dreamy%20atmosphere%2C%20clean%20composition&width=1600&height=900&seq=home-hero-bg&orientation=landscape"
                alt="洞洞杂货铺 漫画"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
            </div>
            <div className="relative z-10 h-full flex items-center">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
                {heroComic && (
                  <>
                    <div className="flex-shrink-0 w-40 md:w-56">
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                        <img
                          src={heroComic.coverImage}
                          alt={`${heroComic.title} 漫画封面`}
                          title={`${heroComic.title} - 89890漫画网免费阅读`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    <div className="flex-1 text-center lg:text-left">
                      <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                        <span className="text-xs px-3 py-1 bg-accent-500 text-background-50 rounded-full font-medium">
                          {heroComic.status}
                        </span>
                        <span className="text-xs px-3 py-1 bg-background-50/20 text-background-50 rounded-full backdrop-blur-sm">
                          {heroComic.tags.slice(0, 2).join(' · ')}
                        </span>
                      </div>
                      <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
                        {heroComic.title}
                      </h1>
                      <p className="text-sm md:text-base text-background-50/80 max-w-lg mx-auto lg:mx-0 leading-relaxed mb-6">
                        {heroComic.description}
                      </p>
                      <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                        <span className="flex items-center gap-1 text-background-50 text-sm">
                          <i className="ri-star-fill text-accent-500"></i>
                          <strong>{heroComic.rating}</strong> 分
                        </span>
                        <span className="text-background-50/60">|</span>
                        <span className="text-background-50/80 text-sm">
                          {heroComic.views >= 10000 ? `${(heroComic.views / 10000).toFixed(1)}万` : heroComic.views} 次阅读
                        </span>
                        <span className="text-background-50/60">|</span>
                        <span className="text-background-50/80 text-sm">
                          {heroComic.chapters.length} 话
                        </span>
                      </div>
                      <div className="flex items-center justify-center lg:justify-start gap-3">
                        <Link
                          to={`/article/${heroComic.id}/${heroComic.chapters[0]?.id}`}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-play-line"></i>
                          开始阅读
                        </Link>
                        <Link
                          to={`/article/${heroComic.id}/${heroComic.chapters[heroComic.chapters.length - 1]?.id}`}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/20 text-background-50 rounded-full font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
                        >
                          <i className="ri-skip-forward-line"></i>
                          最新话
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    漫画分类
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">找到你喜欢的类型，开启阅读之旅</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500 text-center leading-relaxed">
                      {cat.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">大家都在看的精彩漫画</p>
                </div>
                <Link
                  to="/category/rexue"
                  className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {hotComics.map((comic) => comic && (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    最新更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">每日更新，精彩不停</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {newUpdateComics.map((comic) => comic && (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}/${comic.chapters[comic.chapters.length - 1]?.id}`}
                    className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-20 h-[104px] flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} 漫画封面`}
                        title={`${comic.title} - 89890漫画网免费阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          comic.status === '连载中'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-secondary-100 text-secondary-700'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          {comic.rating}
                        </span>
                        <span>
                          {comic.chapters.length} 话
                        </span>
                        <span className="text-primary-500">
                          更新于 {comic.updateDate}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  为什么选择<strong>89890漫画网</strong>？
                </h2>
                <p className="text-sm text-foreground-500 mt-2">为漫迷打造的极致阅读体验</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-book-open-line', title: '免费阅读', desc: '所有漫画完全免费，无需注册即可畅读' },
                  { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '滑动即可连续阅读，便捷省力，沉浸体验' },
                  { icon: 'ri-refresh-line', title: '每日更新', desc: '热门漫画每日更新，精彩内容永不断更' },
                  { icon: 'ri-layout-grid-line', title: '丰富分类', desc: '热血、搞笑、恋爱、奇幻等多种类型应有尽有' },
                ].map((item, idx) => (
                  <div key={idx} className="text-center p-6 rounded-xl bg-background-50">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}